// ======================================================================
// File Name       : ApiUtils
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import { camelizeKeys, decamelize, decamelizeKeys } from 'humps';
import {
  AUTHORIZATION,
  DEV_MODE,
  DRILL_SERVER_URL,
  FRONTGW_SERVER_URL,
  HUBAPI_SERVER_URL,
  IS_USE_MOCK_SERVER,
  MESSAGE_TYPE,
  MOCK_SERVER_URL,
  ROOT_URL,
  STATUS_CD,
  USE_MOCK_API,
  X_SSAPI_VERSION,
  AUTH_OBJECT_USING_API_LIST,
  COMMON_DOMAIN
} from '../constants';
import { LOGOUT } from '../constants/ActionTypes';
import { SHOW_MESSAGE } from '../constants/actionTypes/message';
import store from '../store';
import { hideLoading } from './PageUtils';
import * as SessionStorageUtils from './SessionStorageUtils';
import Cookies from 'universal-cookie';

export async function commonCallApi({
  serverUrl = FRONTGW_SERVER_URL,
  path,
  method = 'POST',
  data,
  institutionId,
  enterpriseId,
  muiltipart,
  drillServer,
  certificateLoginApiGatewayUrl, // #7303
  externalServer, // sprint_20230403 #14
}) {
  const state = store.getState();
  const { authedUser } = state.authed;

  let bodyData = {};

  // Check api use MockServer
  const isRequestMockServer = IS_USE_MOCK_SERVER && USE_MOCK_API.includes(path);
  if (isRequestMockServer) {
    serverUrl = MOCK_SERVER_URL;
    console.log('Using Mockserver: ' + serverUrl + ' , for API ' + path);
  }

  // #7303 START
  if (certificateLoginApiGatewayUrl) {
    serverUrl = `${certificateLoginApiGatewayUrl}/fgwv2`;
  }
  // #7303 END

  // Switch frontgw/drill url
  if (drillServer) serverUrl = DRILL_SERVER_URL;

  if (externalServer) {
    serverUrl = ROOT_URL;
  }

  // set enterprise_id, institution_id
  if (enterpriseId) {
    bodyData.enterpriseId = authedUser.enterpriseId;
  } else if (authedUser.institutionId) {
    bodyData.institutionId = authedUser.institutionId;
  }

  bodyData.authCd = authedUser.authCd;
  // #7385 START
  if (bodyData.authCd) {
    //STEP9.0.0 START
    if (AUTH_OBJECT_USING_API_LIST.includes(path)) {
      var authObj = {'token': authedUser.token};
      bodyData['authentication_info'] = authObj;
    } else {
      bodyData.token = authedUser.token;
    }
    //STEP9.0.0 END
  }
  // #7385 END

  bodyData = { ...bodyData, ...data };
  const payload = decamelizeKeys(bodyData);

  // set headers
  const headers = new Headers({ 'X-SSapi-version': X_SSAPI_VERSION });
  if (!muiltipart) headers.append('Content-Type', 'application/json');
  const body = muiltipart ? getFormData(bodyData) : JSON.stringify(removeNullsInArray(payload)); // #6783

  let response;
  let contentType;
  try {
    response = await fetch(`${serverUrl}/${path}`, {
      method,
      headers,
      body,
      credentials: 'include',
    });
    contentType = response.headers.get('content-type');
    if (isRequestMockServer) contentType = 'application/json' // Set contentType for response MockServer
    if (contentType && contentType.indexOf('application/json') > -1) {
      const jsonRes = await response.json();
      const camlizeJson = camelizeKeys(jsonRes || {});

      if (response.ok) {
        if (DEV_MODE) console.log(camlizeJson);
        return camlizeJson;
      }

      throw camlizeJson;
    }
    // sprint_20230418 #15 END
    return {};
  } catch (error) {
    // sprint_20230619-UT-001 START
    // response ok but there are no json returned
    if (contentType && contentType.indexOf('application/json') > -1 && response.ok) {
      return {};
    }
    // sprint_20230619-UT-001 SEND

    console.log(error);

    let messageInfo = {
      message: ['システムエラーが発生しました。'],
    };

    if (error.statusCd) {
      messageInfo = {
        statusCd: error.statusCd,
        message: error.errMessageList,
      };
    }

    messageInfo.targetModal = !!state.modal.modalId;
    messageInfo.messageType = MESSAGE_TYPE.error;
    if (error.statusCd === STATUS_CD.LOGIN_ERROR && !isRequestMockServer) { // Check using MockServer
      store.dispatch({ type: LOGOUT });
    }
    store.dispatch(hideLoading());
    store.dispatch({ type: SHOW_MESSAGE, ...messageInfo });
    throw messageInfo;
  }
}

function getFormData(object) {
  const decamelizedObject = decamelizeFormData(object);
  return Object.keys(decamelizedObject).reduce((formData, key) => {
    if (decamelizedObject[key] !== undefined) {
      let value = decamelizedObject[key];
      if (!isFile(value) && isObject(value)) {
        value = JSON.stringify(removeNullsInArray(value)); // #6783
      }
      formData.append(key, value);
    }
    return formData;
  }, new FormData());
}

function decamelizeFormData(object) {
  const check = !isObject(object)
    || isDate(object)
    || isRegExp(object)
    || isBoolean(object)
    || isFunction(object)
    || isFile(object);
  if (check) {
    return object;
  }
  let output = null;
  if (isArray(object)) {
    output = [];
    const length = object.length;
    for (let i = 0; i < length; i++) {
      output.push(decamelizeFormData(object[i]));
    }
  } else {
    output = {};
    for (const key in object) {
      if (Object.prototype.hasOwnProperty.call(object, key)) {
        output[decamelize(key)] = decamelizeFormData(object[key]);
      }
    }
  }
  return output;
}

// #6783 START
function isObjectJSon(object) {
  return Object.prototype.toString.call(object) === '[object Object]';
}

function removeNullsInArray(obj) {
  const isArr = isArray(obj);
  if (isArr && obj.length) {
    // Note #6031, #6905: In Newfront, when call api reportOperation/GetList with role 講師 or 採点
    // It will be set a empty value in list as : checker_user_list: ["", "自分のユーザ識別ID"]
    // Therefore in case of empty value "" is set in list of parameter request, leave as it is
    obj = obj.filter(el => (el !== null && el !== undefined));
  } else {
    for (const k in obj) {
      if (isObjectJSon(obj[k]) || isArray(obj[k])) {
        obj[k] = removeNullsInArray(obj[k]);
      }
    }
  }
  return obj;
}
// #6783 END

function isFunction(object) {
  return typeof object === 'function';
}

export function isObject(object) {
  return object === Object(object);
}

function isFile(object) {
  return object instanceof File;
}

function isArray(object) {
  return Object.prototype.toString.call(object) === '[object Array]';
}

function isDate(object) {
  return Object.prototype.toString.call(object) === '[object Date]';
}

function isRegExp(object) {
  return Object.prototype.toString.call(object) === '[object RegExp]';
}

function isBoolean(object) {
  return Object.prototype.toString.call(object) === '[object Boolean]';
}

function isNumber(object) {
  const test = object - 0;
  return test == object;
}

export async function logoutApi({
  serverUrl = FRONTGW_SERVER_URL,
  path,
  method = 'POST',
  data,
  institutionId,
  enterpriseId,
  muiltipart,
  drillServer,
}) {
  const state = store.getState();
  const { authedUser } = state.authed;

  let bodyData = {};

  bodyData.authCd = authedUser.authCd;

  bodyData = { ...bodyData, ...data };
  const payload = decamelizeKeys(bodyData);

  // set headers
  const headers = new Headers({ 'X-SSapi-version': X_SSAPI_VERSION });
  headers.append('Content-Type', 'application/json');
  const body = JSON.stringify(payload);

  try {
    const response = await fetch(`${serverUrl}/${path}`, {
      method,
      headers,
      body,
    });
    return {};
  } catch (error) {
    store.dispatch({ type: LOGOUT });
    return {};
  }
}

export async function downLoadImportFile(url) {
  const headers = new Headers({ 'X-SSapi-version': X_SSAPI_VERSION });
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.log(error);
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
      document.body.removeChild(link);
    }, 10);
    throw error;
  }
}
export async function downLoadFile(url) {
  const headers = new Headers({ 'X-SSapi-version': X_SSAPI_VERSION });
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    const blob = await response.blob();
    return blob;
  } catch (error) {
    throw error;
  }
}

// sprint_20230522_tma START
export async function callHupApi({
  serverUrl = HUBAPI_SERVER_URL,
  path,
  method = 'POST',
  data,
}) {
  const state = store.getState();
  // STEP9.0.0 START
  const { authedUser } = state.authed;

  let bodyData = {};

  bodyData.authCd = authedUser.authCd;
  if (bodyData.authCd) {
    bodyData.smst = true;
    bodyData.token = authedUser.token;
  }
  // STEP9.0.0 END
  bodyData = { ...bodyData, ...data };

  // set headers
  const headers = new Headers({ 'Content-Type': 'application/json' });
  headers.append('authorization', AUTHORIZATION);
  const body = JSON.stringify(bodyData);

  try {
    await fetch(`${serverUrl}/${path}`, {
      method,
      headers,
      body,
    });
    return {};
  } catch (error) {
    let messageInfo = {
      message: ['システムエラーが発生しました。'],
    };

    if (error.statusCd) {
      messageInfo = {
        statusCd: error.statusCd,
        message: error.errMessageList,
      };
    }

    messageInfo.targetModal = !!state.modal.modalId;
    messageInfo.messageType = MESSAGE_TYPE.error;
    store.dispatch(hideLoading());
    store.dispatch({ type: SHOW_MESSAGE, ...messageInfo });
    throw messageInfo;
  }
}
// sprint_20230522_tma END

// Step 10.0.0 START
export async function callPythonApi({
  serverUrl = COMMON_DOMAIN,
  path,
  method = 'POST',
  data,
}) {
  const state = store.getState();
  const { authedUser } = state.authed;

  let bodyData = {};

  bodyData.authCd = authedUser.authCd;
  if (bodyData.authCd) {
    authedUser.token && (bodyData.token = authedUser.token);
  }
  bodyData = { ...bodyData, ...data };
  const payload = decamelizeKeys(bodyData);

  // set headers
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const body = JSON.stringify(removeNullsInArray(payload));

  let response;
  let contentType;
  try {
    response = await fetch(`${serverUrl}/${path}`, {
      method,
      headers,
      body,
    });

    contentType = response.headers.get('content-type');
    if (contentType && contentType.indexOf('application/json') > -1) {
      const jsonRes = await response.json();
      const camlizeJson = camelizeKeys(jsonRes || {});

      if (response.ok) {
        if (DEV_MODE) console.log(camlizeJson);
        return camlizeJson;
      }

      throw camlizeJson;
    }
    return {};
  } catch (error) {
    let messageInfo = {
      message: ['システムエラーが発生しました。'],
    };

    if (error.statusCd) {
      messageInfo = {
        statusCd: error.statusCd,
        message: error.errMessageList,
      };
    }

    messageInfo.targetModal = !!state.modal.modalId;
    messageInfo.messageType = MESSAGE_TYPE.error;
    store.dispatch(hideLoading());
    store.dispatch({ type: SHOW_MESSAGE, ...messageInfo });
    throw messageInfo;
  }
}
// Step 10.0.0 END
